/**
 * The three steps, and what the rail says about each of them.
 *
 * Owner decision D8, 17 September 2026: **Podstawowe informacje / Ciało i sprzęt / Materiały.** The
 * rail is the ElevenLabs kind that TEACHES — a dot, a title and a two-line description of what the
 * step is for — which is why no step needs a lead paragraph above its form.
 *
 * ── An answered step ECHOES its answer, and the echo is DATA ────────────────────────────────
 * A step behind the trainer replaces its description with what they said: the movement's name and
 * „Masa ciała, 2 metryki" under step 1, the muscles and the equipment under step 2. That is what
 * makes the rail worth its 248px once the trainer is past the first screen.
 *
 * It is returned as STRUCTURE rather than as sentences. „2 metryki" is a Polish plural with three
 * forms and the equipment names are backend data; neither can travel through the key-only translate
 * this layer is allowed. `ui/WizardRail` composes them.
 *
 * ── Step 3 echoes nothing, deliberately ─────────────────────────────────────────────────────
 * It is the last step, so it is never behind the trainer while the wizard is open — and everything
 * on it is optional, so there is no answer to report. The canon's own `G_STEPS` says the same with
 * an empty echo.
 */

import type { MuscleGroup } from '~/entities/exercise-library';

import type { ExerciseDraft } from './exerciseDraft';
import type { CreatableExerciseType } from './exerciseType';

/** The three, in order. */
export type WizardStep = 'basics' | 'body' | 'materials';

export const WIZARD_STEPS: readonly WizardStep[] = ['basics', 'body', 'materials'];

/** What one rail item is drawing. */
export interface WizardStepView {
  readonly step: WizardStep;
  /** 1-based, for the dot and for `aria-posinset`. */
  readonly position: number;
  readonly state: 'done' | 'current' | 'ahead';
  /** The answer to echo, or `null` while the step is current or still ahead. */
  readonly echo: WizardStepEcho | null;
}

/** The facts a finished step reports, in the shape `ui/` can word. */
export type WizardStepEcho =
  | {
      readonly step: 'basics';
      /** Empty when the trainer walked past without naming it — `ui/` says „bez nazwy". */
      readonly name: string;
      readonly exerciseType: CreatableExerciseType;
      readonly metricCount: number;
    }
  | {
      readonly step: 'body';
      readonly primary: readonly MuscleGroup[];
      /** Backend DATA, already localised. The rail prints these; it never looks them up. */
      readonly equipment: readonly string[];
    };

/** Where the draft stands, as the rail draws it. */
export function wizardStepViews(
  draft: ExerciseDraft,
  current: WizardStep,
  equipmentNames: readonly string[],
): readonly WizardStepView[] {
  const currentIndex = WIZARD_STEPS.indexOf(current);
  return WIZARD_STEPS.map((step, index) => ({
    step,
    position: index + 1,
    state: index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'ahead',
    echo: index < currentIndex ? echoOf(step, draft, equipmentNames) : null,
  }));
}

function echoOf(
  step: WizardStep,
  draft: ExerciseDraft,
  equipmentNames: readonly string[],
): WizardStepEcho | null {
  if (step === 'basics') {
    return {
      step: 'basics',
      name: draft.name.trim(),
      exerciseType: draft.type,
      metricCount: draft.metrics.fields.length,
    };
  }
  if (step === 'body') {
    return { step: 'body', primary: draft.primary, equipment: equipmentNames };
  }
  return null;
}

/** The step after this one, or `null` on the last. */
export function nextStep(step: WizardStep): WizardStep | null {
  return WIZARD_STEPS[WIZARD_STEPS.indexOf(step) + 1] ?? null;
}

/** The step before this one, or `null` on the first. */
export function previousStep(step: WizardStep): WizardStep | null {
  const index = WIZARD_STEPS.indexOf(step) - 1;
  return index < 0 ? null : (WIZARD_STEPS[index] ?? null);
}
